<script setup lang="ts">
  import { onMounted, onUnmounted, watch, nextTick } from 'vue'
  import { driver, type DriveStep, type Driver, type PopoverDOM } from 'driver.js'
  import 'driver.js/dist/driver.css'
  import '@/assets/driverjs.css'
  import { useTranslation } from '@/composables/useTranslation'

  const props = defineProps<{
    forceShow?: boolean
    isParticipantSelected?: boolean
    isMatchSelected?: boolean
    selectedMatchMode?: 'link' | 'create' | null
  }>()

  const emit = defineEmits<{
    tourCompleted: []
    selectFirstParticipant: []
    selectFirstMatch: []
  }>()

  const ONBOARDING_COMPLETED_KEY = 'actaport-party-matching-tour-completed'

  const { t } = useTranslation({
    en: {
      // Step 0 - Intro Modal
      introTitle: 'This is a new feature!',
      introDescription: 'You can now avoid exporting duplicate participants.',
      startTourButton: 'Start the tour',
      skipTourButton: 'Skip',

      // Step 1 - Participant Selection
      step1Title: 'Select the participant',
      step1Description:
        'Possible contact matches have been found for this participant. Select the participant to view the matches before exporting them.',

      // Step 2 - Matches Panel
      step2Title: 'Link a match or create a new participant',
      step2Description:
        'Here you can view all contact matches found in Actaport. Select a match to link to, or create a new contact if none are a match.',

      // Step 3A - Link Flow
      step3aTitle: 'Confirm and link',
      step3aDescription:
        'Select the appropriate contact match and click on "Link" to continue. If none of them match, select "Create new participant".',

      // Step 3B - Create Flow
      step3bTitle: 'Create a new participant',
      step3bDescription:
        'If no match is found, create a new participant. This will be saved as a new contact in Actaport.',

      // Navigation buttons
      nextButton: 'Next',
      prevButton: 'Back',
      doneButton: 'Done',
      closeButton: 'Close'
    },
    de: {
      // Step 0 - Intro Modal
      introTitle: 'Das ist eine neue Funktion!',
      introDescription: 'Sie können nun vermeiden, Beteiligte doppelt zu exportieren.',
      startTourButton: 'Tour starten',
      skipTourButton: 'Überspringen',

      // Step 1 - Participant Selection
      step1Title: 'Beteiligten auswählen',
      step1Description:
        'Für diesen Beteiligten wurden mögliche Kontakttreffer gefunden. Wähle ihn aus, um die Treffer vor dem Export zu prüfen.',

      // Step 2 - Matches Panel
      step2Title: 'Treffer verknüpfen oder neuen Beteiligten anlegen',
      step2Description:
        'Hier sehen Sie alle in Actaport gefundenen Kontakttreffer. Wählen Sie einen aus, um ihn zu verknüpfen. Alternativ können Sie auch einen neuen Beteiligten anlegen, falls keiner passt.',

      // Step 3A - Link Flow
      step3aTitle: 'Bestätigen und verknüpfen',
      step3aDescription:
        'Wählen Sie den passenden Kontakttreffer aus und klicken Sie auf „Verknüpfen", um fortzufahren. Wenn keiner passt, wählen Sie „Neuen Beteiligten anlegen".',

      // Step 3B - Create Flow
      step3bTitle: 'Neuen Beteiligten anlegen',
      step3bDescription:
        'Wenn kein Kontakttreffer passt, legen Sie einen neuen Beteiligten an. Dieser wird als neuer Kontakt in Actaport gespeichert.',

      // Navigation buttons
      nextButton: 'Weiter',
      prevButton: 'Zurück',
      doneButton: 'Fertig',
      closeButton: 'Schließen'
    }
  })

  let driverObj: Driver | null = null

  const updateStep3Content = (popover: PopoverDOM): void => {
    if (popover.title) {
      popover.title.textContent =
        props.selectedMatchMode === 'create' ? t('step3bTitle') : t('step3aTitle')
    }

    if (popover.description) {
      popover.description.textContent =
        props.selectedMatchMode === 'create' ? t('step3bDescription') : t('step3aDescription')
    }
  }

  const markTourAsCompleted = (): void => {
    try {
      localStorage.setItem(ONBOARDING_COMPLETED_KEY, 'true')
    } catch (error) {
      console.error('Failed to mark tour as completed', error)
    }
  }

  const hasCompletedTour = (): boolean => {
    if (props.forceShow) {
      return false
    }
    return localStorage.getItem(ONBOARDING_COMPLETED_KEY) === 'true'
  }

  const getBaseSteps = (): DriveStep[] => {
    const steps: DriveStep[] = [
      // Step 1 - Participant Selection
      {
        element: '[data-cy="party-card"]',
        popover: {
          title: t('step1Title'),
          description: t('step1Description'),
          side: 'right',
          align: 'start'
        }
      },
      // Step 2 - Matches Panel
      {
        element: '[data-cy="match-selection-panel"]',
        popover: {
          title: t('step2Title'),
          description: t('step2Description'),
          side: 'left',
          align: 'start'
        }
      },
      // Step 3 - Link or create new participant - Placeholder
      {
        element: '[data-cy="link-button"], [data-cy="create-button"]',
        popover: {
          title: t('step3aTitle'),
          description: t('step3aDescription'),
          side: 'top',
          align: 'center'
        }
      }
    ]
    return steps
  }

  const showIntroModal = (): void => {
    if (hasCompletedTour()) {
      return
    }

    try {
      driverObj = driver({
        showProgress: false,
        steps: [
          {
            popover: {
              title: t('introTitle'),
              description: t('introDescription'),
              nextBtnText: t('startTourButton')
            }
          }
        ],
        onDestroyed: () => {
          // Don't mark as completed yet, only when main tour is done
          driverObj = null
        },
        onNextClick: () => {
          driverObj?.destroy()
          startMainTour()
        },
        nextBtnText: t('startTourButton'),
        onPopoverRender: (popover) => {
          if (popover.previousButton) {
            popover.previousButton.remove()
          }
        }
      })

      driverObj.drive()
    } catch (error) {
      console.error('Failed to show intro modal', error)
    }
  }

  const startMainTour = (): void => {
    try {
      const steps = getBaseSteps()

      driverObj = driver({
        showProgress: true,
        showButtons: ['next', 'previous', 'close'],
        steps,
        onDestroyed: () => {
          markTourAsCompleted()
          emit('tourCompleted')
        },
        onNextClick: (_element, _step, opts) => {
          const currentIndex = opts.state.activeIndex ?? 0
          const totalSteps = steps.length

          // If on last step, complete the tour
          if (currentIndex === totalSteps - 1) {
            driverObj?.destroy()
            return
          }

          // On step 1, if no participant is selected, select the first one
          if (currentIndex === 0 && !props.isParticipantSelected) {
            emit('selectFirstParticipant')
            // Wait for Vue to update the reactive prop, then proceed
            nextTick(() => {
              opts.driver.moveNext()
            })
            return
          }

          // On step 2, if no match is selected, select the first one
          if (currentIndex === 1 && !props.isMatchSelected) {
            emit('selectFirstMatch')
            // Wait for Vue to update the reactive prop, then proceed
            nextTick(() => {
              opts.driver.moveNext()
            })
            return
          }
          opts.driver.moveNext()
        },
        onPopoverRender: (popover, opts) => {
          const currentIndex = opts.state.activeIndex ?? 0

          // Update step 3 title and description dynamically based on selectedMatchMode
          if (currentIndex === 2) {
            updateStep3Content(popover)
          }
        },
        nextBtnText: t('nextButton'),
        prevBtnText: t('prevButton'),
        doneBtnText: t('doneButton')
      })

      driverObj.drive()
    } catch (error) {
      console.error('Failed to start main tour', error)
    }
  }

  const isTourActive = (): boolean => {
    return driverObj !== null && !hasCompletedTour()
  }

  const destroyTour = (): void => {
    if (driverObj) {
      try {
        driverObj.destroy()
        driverObj = null
      } catch (error) {
        console.error('Failed to destroy tour', error)
      }
    }
  }

  watch(
    () => [props.isParticipantSelected, props.isMatchSelected, props.selectedMatchMode],
    (newValues, oldValues) => {
      if (!driverObj || !isTourActive()) {
        return
      }

      const currentIndex = driverObj.getActiveIndex() ?? 0
      const [wasParticipantSelected, wasMatchSelected] = oldValues || [false, false]
      const [isParticipantSelected, isMatchSelected] = newValues

      // Auto-advance when participant is selected on step 1
      if (currentIndex === 0 && !wasParticipantSelected && isParticipantSelected) {
        nextTick(() => {
          driverObj?.moveNext()
        })
        return
      }

      // Auto-advance when match is selected on step 2
      if (currentIndex === 1 && !wasMatchSelected && isMatchSelected) {
        nextTick(() => {
          driverObj?.moveNext()
        })
        return
      }

      // Update step 3 content when on step 3
      const state = driverObj.getState()
      const popover = state?.popover
      if (popover && currentIndex === 2) {
        updateStep3Content(popover)
      }
    }
  )

  onMounted(() => {
    if (hasCompletedTour()) {
      return
    }

    showIntroModal()
  })

  onUnmounted(() => {
    destroyTour()
  })

  defineExpose({
    showIntroModal,
    startMainTour,
    isTourActive,
    destroyTour
  })
</script>

<template>
  <div></div>
</template>
